import { useState } from "react";
import { Button, Popover, PopoverContent, PopoverTitle, PopoverTrigger } from "@opencloud/ui";
import { RotateCcw, X } from "lucide-react";
import { agentBrand } from "../brand.js";
import { taskListPresentation, type latestConversationPlan } from "../task-presentation.js";
import { TaskPlanIcon, TaskPlanItems, TaskProgress } from "./TaskListRenderer.js";

export function ComposerTaskPlan({ plan, failureSummary, onRetry, readOnly = false }: {
  plan: NonNullable<ReturnType<typeof latestConversationPlan>>;
  failureSummary?: string;
  onRetry?: (runId: string) => void;
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const view = taskListPresentation(plan.taskList, plan.lifecycle, plan.run.status);
  const canRetry = !readOnly && !!onRetry && plan.lifecycle === "failed" && plan.run.checkpointVersion > 0;
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger render={<Button type="button" variant="ghost" />}
      className="a3-composer-pill a3-composer-plan" data-run-id={plan.run.id} data-state={plan.lifecycle} data-tone={view.tone}
      aria-label={`Agent plan: ${view.status}. ${view.title}. ${view.count}.`}
      title={`${view.status} · ${view.title} · ${view.count}`}>
      <span className="a3-composer-plan-icon" aria-hidden="true"><TaskPlanIcon view={view} /></span>
      <span>Tasks</span><span className="a3-composer-plan-count" aria-hidden="true">{view.completed} / {plan.taskList.items.length}</span>
      {view.status !== "In progress" && view.status !== "Completed" ? <span className="a3-composer-plan-status">{view.status}</span> : null}
    </PopoverTrigger>
    <PopoverContent side="top" align="start" sideOffset={8} className="a3-theme a3-plan-popover"
      data-oc-theme={agentBrand().themeId} data-state={plan.lifecycle} data-tone={view.tone}>
      <div className="a3-plan-popover-heading"><div><PopoverTitle>Agent plan</PopoverTitle><p>{view.status} · {view.count}</p></div>
        <Button type="button" size="icon" variant="ghost" aria-label="Close agent plan" onClick={() => setOpen(false)}><X aria-hidden="true" /></Button>
      </div>
      <TaskProgress taskList={plan.taskList} />
      <div className="a3-plan-content" tabIndex={0} aria-label="Scroll agent tasks">
        <TaskPlanItems taskList={plan.taskList} lifecycle={plan.lifecycle} runStatus={plan.run.status} />
      </div>
      {plan.lifecycle === "failed" && !readOnly ? <div className="a3-plan-recovery">
        {failureSummary ? <p>{failureSummary}</p> : null}
        {canRetry ? <Button type="button" variant="ghost" onClick={() => { setOpen(false); onRetry?.(plan.run.id); }}>
          <RotateCcw aria-hidden="true" />Retry from saved work
        </Button> : <p>{readOnly ? "The app owner can continue this work." : "Send a message to continue."}</p>}
      </div> : null}
    </PopoverContent>
  </Popover>;
}
